import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import AppLayout from '../../components/AppLayout';
import { S } from '../regureStyles';
import useOffersFunctions, { toDisplay } from './useOffersFunctions';
import OfferDetailPanel from './OfferDetailPanel';
import NewOfferPanel from './NewOfferPanel';
import RateTablesPanel from './RateTablesPanel';
import Header from './Header';
import Filter from './Filter';
import Table from './Table';
import Pagination from './Pagination';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const ADMIN_ROLES = new Set(['admin', 'systemAdmin', 'orgAdmin', 'org']);

const OffersScreen = () => {
  const { t } = useTranslation();
  const {
    offers,
    total,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    productFilter,
    setProductFilter,
    brokerFilter,
    setBrokerFilter,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    activeFilters,
    hasActiveFilters,
    clearFilters,
    showNew,
    setShowNew,
    selectedOffer,
    setSelectedOffer,
    page,
    setPage,
    pageSize,
    setPageSize,
    loading,
    STATUS_OPTIONS,
    STATUS_LABELS,
    fetchOffers,
    handleCloseNew,
    handleCreated,
    filtered,
    paginated,
    totalPages,
    convRate,
  } = useOffersFunctions();

  const userLogin = useSelector((state) => state.userLogin);
  const user = userLogin?.userInfo?.data?.user || userLogin?.userInfo?.data?.data || {};
  const isAdmin = ADMIN_ROLES.has(user.role);

  const [tab, setTab] = useState('offers');

  // Batch-export selection, as offer _id (never offerNumber — the export
  // endpoint matches on _id).
  //
  // It lives HERE rather than inside Table because the table only ever
  // holds one page: the server paginates, so a Set owned by the row
  // renderer would drop page 1's ticks the moment you looked at page 2.
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const toggleRow = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const togglePage = useCallback((ids, select) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (select ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // A selection survives paging but NOT a change of filter. Ticking ten
  // drafts and then switching the status to Accepted would otherwise leave
  // ten now-invisible rows armed for export, and a set the user can no
  // longer see is a set they cannot check before spending it.
  const filterKey = JSON.stringify(activeFilters);
  useEffect(() => { setSelectedIds(new Set()); }, [filterKey]);

  // Offer being revised. Set from the detail panel's Revise action; opens
  // the New Offer panel seeded with this offer's inputs so nothing has to
  // be re-entered. Cleared whenever that panel closes.
  const [reviseFrom, setReviseFrom] = useState(null);
  // Draft being edited in place. Opens the SAME quote form as Revise,
  // seeded identically — the difference is that saving updates the draft
  // rather than writing a new version, so the draft keeps its id, its
  // number and its single row in the list (tracker R32, 2026-09-04).
  const [editDraft, setEditDraft] = useState(null);

  if (loading) {
    return (
      <AppLayout>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
          <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      </AppLayout>
    );
  }

  const tabBtn = (key, label) => {
    const active = tab === key;
    return (
      <button
        key={key}
        onClick={() => setTab(key)}
        style={{
          padding: '10px 16px',
          background: 'transparent',
          border: 'none',
          borderBottom: `2px solid ${active ? S.burgundy : 'transparent'}`,
          fontSize: 13,
          fontWeight: 700,
          color: active ? S.burgundy : S.textMuted,
          cursor: 'pointer',
          transition: 'color 0.15s, border-color 0.15s',
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <AppLayout>
      {(showNew || reviseFrom || editDraft) && (
        <NewOfferPanel
          reviseFrom={reviseFrom}
          editDraft={editDraft}
          onClose={() => { setReviseFrom(null); setEditDraft(null); handleCloseNew(); }}
          onCreated={(created) => { setReviseFrom(null); setEditDraft(null); handleCreated(created); }}
        />
      )}
      {selectedOffer && (
        <OfferDetailPanel
          offer={selectedOffer}
          onClose={() => setSelectedOffer(null)}
          onRevise={(src) => { setSelectedOffer(null); setReviseFrom(src); }}
          onEditDraft={(src) => { setSelectedOffer(null); setEditDraft(src); }}
          // Version-history links: swap the panel to another offer in the
          // same revision chain without closing it, so the chain can be
          // walked. Falls back silently if the offer is out of scope.
          onOpenOffer={async (offerId) => {
            if (!offerId) return;
            try {
              const r = await axios.get(`${BASE_URL}/offers/${offerId}`, { withCredentials: true });
              if (r.data?.data) setSelectedOffer(toDisplay(r.data.data, t));
            } catch { /* leave the panel on the current offer */ }
          }}
          onUpdated={(updated) => {
            // D-18: swap the panel's offer in-place with the patch response
            // so amended fields (notes / expiresAt) render immediately
            // instead of after a close-reopen cycle. Run through toDisplay
            // first so aliased fields (created ← createdAt, expires ←
            // expiresAt, broker display name) exist — otherwise the header
            // showed "—" for CREATED and EXPIRES until the user closed and
            // reopened the panel (2026-08-06 draft-offer edit follow-up).
            if (updated && updated._id) setSelectedOffer(toDisplay(updated, t));
            fetchOffers();
          }}
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Header
          setShowNew={setShowNew}
          offers={offers}
          total={total}
          convRate={convRate}
          filters={activeFilters}
          selectedIds={selectedIds}
          onExported={clearSelection}
        />

        {isAdmin && (
          <div style={{ display: 'flex', gap: 4, padding: '0 32px', borderBottom: `1px solid ${S.borderLight}`, background: '#fff' }}>
            {tabBtn('offers', t('offers.list.tabOffers'))}
            {tabBtn('rate-tables', t('offers.list.tabRateTables'))}
          </div>
        )}

        {tab === 'offers' ? (
          <>
            <Filter
              search={search}
              setSearch={setSearch}
              setPage={setPage}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              productFilter={productFilter}
              setProductFilter={setProductFilter}
              brokerFilter={brokerFilter}
              setBrokerFilter={setBrokerFilter}
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
              clearFilters={clearFilters}
              hasActiveFilters={hasActiveFilters}
              STATUS_OPTIONS={STATUS_OPTIONS}
              STATUS_LABELS={STATUS_LABELS}
              filtered={filtered}
            />
            {selectedIds.size > 0 && (
              // The selection is spendable from the header button, so it has
              // to be visible from anywhere on the page — including page 4,
              // where every ticked row was on page 1.
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 32px', background: 'rgba(128,0,32,0.05)',
                borderBottom: `1px solid ${S.borderLight}`,
              }}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: S.burgundy }}>
                  {t('offers.list.selectionCount', { count: selectedIds.size })}
                </span>
                <button
                  onClick={clearSelection}
                  style={{
                    background: 'none', border: 'none', padding: 0,
                    fontSize: 12, fontWeight: 600, color: S.textMuted,
                    cursor: 'pointer', textDecoration: 'underline',
                  }}
                >
                  {t('offers.list.selectionClear')}
                </button>
              </div>
            )}
            <Table
              paginated={paginated}
              setSelectedOffer={setSelectedOffer}
              setShowNew={setShowNew}
              selectedIds={selectedIds}
              toggleRow={toggleRow}
              togglePage={togglePage}
            />
            <Pagination paginated={paginated} page={page} setPage={setPage} filtered={filtered} totalPages={totalPages} total={total} pageSize={pageSize} setPageSize={setPageSize} />
          </>
        ) : (
          <RateTablesPanel />
        )}
      </div>
    </AppLayout>
  );
};

export default OffersScreen;
